// components/CarouselClient.tsx
'use client';

import { useEffect } from 'react';

export default function CarouselClient({ carouselId }: { carouselId: string }) {
  useEffect(() => {
    const carousel = document.getElementById(carouselId) as HTMLElement;
    let carouselInstance: any = null;
    let cleanupInternalListeners: (() => void) | null = null;
    let scrollRafId: number | null = null;
    let metricsRafId: number | null = null;
    let updateRafId: number | null = null;
    let initRafId1: number | null = null;
    let initRafId2: number | null = null;
    let resizeObserver: ResizeObserver | null = null;

    if (carousel) {
      const track = carousel.querySelector<HTMLElement>(`.${carouselId}-track`);
      const slides = carousel.querySelectorAll<HTMLElement>(`.${carouselId}-slide`);

      if (!track || slides.length === 0) return;

      // Cache measurements so we don't force layout on every scroll.
      const metricsRef = {
        slideWidth: 0,
        gap: 0,
        totalWidth: 0,
      };
      let latestObservedSlideWidth = 0;
      let hasInteractedWithTrack = false;
      // Cache window.innerWidth so we read it once per resize rather than on every scroll.
      let cachedViewportWidth = window.innerWidth;
      const getResponsiveNumber = (
        mobile?: string,
        tablet?: string,
        desktop?: string,
        fallback = 1,
      ) => {
        const w = cachedViewportWidth;
        const raw = w >= 1024 ? desktop : w >= 768 ? tablet : mobile;
        const parsed = parseFloat(raw || '');
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
      };

      const prefersReduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = () => (prefersReduced() ? 'auto' : 'smooth') as ScrollBehavior;

      const getGap = () => {
        const parsed = parseFloat(carousel.dataset.gap || '');
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      };

      const measureMetrics = () => {
        const slideWidth = latestObservedSlideWidth || 0;
        const gap = getGap();
        const totalWidth = slideWidth + gap;
        metricsRef.slideWidth = slideWidth;
        metricsRef.gap = gap;
        metricsRef.totalWidth = totalWidth;
      };

      const readPerView = () => {
        return getResponsiveNumber(
          carousel.dataset.perViewMobile,
          carousel.dataset.perViewTablet,
          carousel.dataset.perViewDesktop,
          1,
        );
      };

      // Cache perView so scroll-driven updates don't read computed styles.
      let perViewCached = readPerView();
      const refreshPerView = () => {
        perViewCached = readPerView();
      };
      const scheduleUpdateControls = () => {
        if (updateRafId != null) return;
        updateRafId = requestAnimationFrame(() => {
          updateRafId = null;
          updateControls();
        });
      };

      const scheduleMetricsRefresh = (slideWidth?: number) => {
        if (typeof slideWidth === 'number' && Number.isFinite(slideWidth)) {
          latestObservedSlideWidth = slideWidth;
        }
        if (metricsRafId != null) return;
        metricsRafId = requestAnimationFrame(() => {
          metricsRafId = null;
          refreshPerView();
          measureMetrics();
          // During initial/resize refresh, avoid sync scroll position reads.
          updateControls(false);
        });
      };

      const getSnapMode = () => {
        const w = cachedViewportWidth;
        const raw =
          w >= 1024
            ? carousel.dataset.snapModeDesktop
            : w >= 768
              ? carousel.dataset.snapModeTablet
              : carousel.dataset.snapModeMobile;
        return (raw as 'none' | 'item' | 'group') ?? 'group';
      };

      const getSnapStep = (perView: number) => {
        const w = cachedViewportWidth;
        const raw =
          w >= 1024
            ? carousel.dataset.snapStepDesktop
            : w >= 768
              ? carousel.dataset.snapStepTablet
              : carousel.dataset.snapStepMobile;
        const parsed = parseInt(raw || '', 10);
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 4) return parsed;
        return Math.max(1, Math.min(4, Math.round(perView)));
      };

      const getStepSize = (perView: number) => {
        const snapMode = getSnapMode();
        if (snapMode !== 'group') return 1;
        return getSnapStep(perView);
      };

      function currentIndex(allowLayoutRead = true) {
        if (slides.length === 0 || !track) return 0;
        const totalWidth = metricsRef.totalWidth;
        if (!totalWidth) return 0;
        if (!allowLayoutRead || !hasInteractedWithTrack) return 0;
        return Math.round(track.scrollLeft / totalWidth);
      }

      function goToIndex(i: number) {
        if (slides.length === 0) return;
        const clamped = Math.max(0, Math.min(i, slides.length - 1));

        // Calculate the exact scroll position instead of using scrollIntoView.
        // Use cached measurements to avoid forced reflow on scroll.
        if (!metricsRef.totalWidth) scheduleMetricsRefresh();
        const totalWidth = metricsRef.totalWidth || 0;
        if (!totalWidth) return;
        const targetScrollLeft = clamped * totalWidth;
        hasInteractedWithTrack = true;

        // Use direct scrollTo for more control
        track?.scrollTo({
          left: targetScrollLeft,
          behavior: behavior(),
        });
      }

      function updateControls(allowLayoutRead = true) {
        const current = currentIndex(allowLayoutRead);
        const maxIndex = slides.length - 1;

        const perView = perViewCached;
        const maxScrollIndex = Math.min(maxIndex, Math.max(0, Math.ceil(slides.length - perView)));
        const snapMode = getSnapMode();
        const snapStep = getSnapStep(perView);
        const step = snapMode === 'group' ? snapStep : 1;

        const canGoPrev = current > 0;
        const canGoNext = current < maxScrollIndex;

        carousel.dataset.currentIndex = String(current);
        carousel.dataset.maxScrollIndex = String(maxScrollIndex);
        carousel.dataset.canGoPrev = canGoPrev ? 'true' : 'false';
        carousel.dataset.canGoNext = canGoNext ? 'true' : 'false';

        // Dispatch update event for external controls (including dots)
        if (carouselId) {
          const event = new CustomEvent('carousel-update', {
            detail: {
              carouselId,
              currentIndex: current,
              maxIndex,
              maxScrollIndex,
              perView,
              snapMode,
              snapStep,
              step,
              slidesLength: slides.length,
              canGoPrev,
              canGoNext,
            },
          });
          document.dispatchEvent(event);
        }
      }

      // Store carousel functions for external control
      carouselInstance = {
        goToIndex,
        currentIndex,
        updateControls,
        track,
        slides,
        getStepSize: () => getStepSize(perViewCached),
        getSnapMode,
      };

      // Sync controls on scroll
      const handleScroll = () => {
        hasInteractedWithTrack = true;
        if (scrollRafId != null) return;
        scrollRafId = requestAnimationFrame(() => {
          scrollRafId = null;
          updateControls(true);
        });
      };
      const handleResize = () => {
        cachedViewportWidth = window.innerWidth;
        scheduleMetricsRefresh();
      };
      const handleKeydown = (e: KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToIndex(currentIndex(true) + 1);
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToIndex(currentIndex(true) - 1);
        }
      };

      track.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleResize);
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(entries => {
          let hasObservedChange = false;
          for (const entry of entries) {
            if (entry.target === slides[0]) {
              latestObservedSlideWidth = entry.contentRect.width || 0;
              hasObservedChange = true;
            } else if (entry.target === track) {
              hasObservedChange = true;
            }
          }
          if (hasObservedChange) {
            scheduleMetricsRefresh();
          }
        });

        if (slides[0]) resizeObserver.observe(slides[0]);
        resizeObserver.observe(track);
      }

      // Initial sync – defer to after the first paint to keep
      // setup work outside the critical rendering path.
      initRafId1 = requestAnimationFrame(() => {
        initRafId2 = requestAnimationFrame(() => {
          scheduleMetricsRefresh();
        });
      });

      // Keyboard navigation
      carousel.addEventListener('keydown', handleKeydown);

      // Mark as initialized
      carousel.setAttribute('data-carousel-initialized', 'true');

      cleanupInternalListeners = () => {
        if (scrollRafId != null) {
          cancelAnimationFrame(scrollRafId);
          scrollRafId = null;
        }
        if (metricsRafId != null) {
          cancelAnimationFrame(metricsRafId);
          metricsRafId = null;
        }
        if (updateRafId != null) {
          cancelAnimationFrame(updateRafId);
          updateRafId = null;
        }
        if (initRafId1 != null) {
          cancelAnimationFrame(initRafId1);
          initRafId1 = null;
        }
        if (initRafId2 != null) {
          cancelAnimationFrame(initRafId2);
          initRafId2 = null;
        }
        resizeObserver?.disconnect();
        resizeObserver = null;
        track.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
        carousel.removeEventListener('keydown', handleKeydown);
      };
    }

    // Handle external control events
    const handleExternalControl = (event: CustomEvent) => {
      const { carouselId: targetCarouselId, action, index } = event.detail;

      // Only respond to events for this specific carousel
      if (targetCarouselId !== carouselId) return;

      if (!carouselInstance) return;

      const current = carouselInstance.currentIndex();
      const step = carouselInstance.getStepSize?.() ?? 1;
      const maxScrollIndexRaw = parseInt(carousel.dataset.maxScrollIndex || '0', 10);
      const maxScrollIndex = Number.isFinite(maxScrollIndexRaw) ? maxScrollIndexRaw : 0;
      const snapMode = carouselInstance.getSnapMode?.() ?? 'group';

      const goPrevIndex = () => {
        if (snapMode !== 'group' || step <= 1) return Math.max(0, current - 1);
        if (current === maxScrollIndex && current % step !== 0) {
          return Math.max(0, Math.floor((current - 1) / step) * step);
        }
        return Math.max(0, current - step);
      };

      const goNextIndex = () => {
        if (snapMode !== 'group' || step <= 1) return Math.min(maxScrollIndex, current + 1);
        const candidate = current + step;
        return candidate >= maxScrollIndex ? maxScrollIndex : candidate;
      };

      if (action === 'prev') {
        carouselInstance.goToIndex(goPrevIndex());
      } else if (action === 'next') {
        carouselInstance.goToIndex(goNextIndex());
      } else if (action === 'goto' && typeof index === 'number') {
        carouselInstance.goToIndex(index);
      }
    };

    document.addEventListener('carousel-control', handleExternalControl as EventListener);

    // Cleanup
    return () => {
      document.removeEventListener('carousel-control', handleExternalControl as EventListener);
      cleanupInternalListeners?.();
    };
  }, [carouselId]);

  return null;
}
